import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  ViewChild,
  TemplateRef,
  ViewContainerRef,
  inject,
  DestroyRef,
} from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { TemplatePortal } from '@angular/cdk/portal';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSort } from '@angular/material/sort';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { noop, finalize, filter } from 'rxjs';

import { GridApi } from 'ag-grid-enterprise';
import { GridReadyEvent, SortChangedEvent } from 'ag-grid-community';
import { TsTableService } from 'tsui';
import { TableActions } from 'tsui/lib/cross-cutting/models/table-actions';
import { ColumnDef, DEFAULT_GRID_OPTIONS } from 'tsui/lib/ts-table/ts-table.model';
import { FieldDef } from '@core/models';
import { FeatureFlag } from '@core/enums';
import { RelatedToType } from '@shared/enums';
import { UserState } from '@state/user/state';
import { AccountState } from '@state/account/state';
import { Permission } from '@core/enums/permission';
import { ProjectTemplate } from 'app/modules/projects/projects.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { removeArchivedFieldsFromTableDefinition } from '@shared/functions';
import { OffersService } from 'app/modules/projects/details/offers/offers.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { AddOfferComponent } from 'app/modules/projects/details/offers/add-offer/add-offer.component';
import { PanelsInterfaceComponent, PanelsInterfaceComponentBase } from '../panels-interface.component';
import { Offer, OfferWorkflowStateEnum, OffersDialogDataModel } from 'app/modules/projects/details/offers/offers.types';
import { TableStatusRenderer } from '@shared/components/ts-table-renderers/table-status-renderer/table-status-renderer.component';

@Component({
  selector: 'project-panel-offers',
  templateUrl: './offers.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PanelsOffersComponent extends PanelsInterfaceComponentBase implements PanelsInterfaceComponent, OnInit {
  displayColumns: string[];
  columnDefs: ColumnDef[] = [];
  rowData: any[] = [];
  sortState: string;
  newTable: boolean;
  gridOptions = {
    ...DEFAULT_GRID_OPTIONS,
  };
  actions: TableActions[] = [
    {
      label: 'Edit',
      action: (item) => this.openEditOfferModel(item),
      icon: 'edit',
    },
  ];

  offersDataSource: MatTableDataSource<Offer>;
  offerWorkflowStateEnum = OfferWorkflowStateEnum;
  filteredFieldDefs: FieldDef[] = [];

  private _projectTemplate: ProjectTemplate<any>;
  private _fieldDefsPanelOverlayRef: OverlayRef;
  private _fieldsDefsPanelTemplatePortal: TemplatePortal;
  private gridApi: GridApi;

  private destroyRef = inject(DestroyRef);

  @ViewChild('fieldDefsPanelOrigin') private _fieldDefsPanelOrigin: any;
  @ViewChild('fieldDefsPanel') private _fieldDefsPanel: TemplateRef<any>;
  @ViewChild(MatSort) private sort: MatSort;

  private get fieldDefs(): FieldDef[] {
    return this.store.selectSnapshot(
      ObjectsManagerState.getFieldsByObjectName(RelatedToType.Offer, {
        allowedFieldsOnly: true,
        addNestedFields: false,
      }),
    );
  }

  constructor(
    private readonly matDialog: MatDialog,
    protected readonly cdRef: ChangeDetectorRef,
    private readonly overlay: Overlay,
    private readonly viewContainerRef: ViewContainerRef,
    private readonly offersService: OffersService,
    private readonly projectsService: ProjectsService,
  ) {
    super(cdRef);

    this.account = this.store.selectSnapshot(AccountState.getAccount);

    this.filteredFieldDefs = [...this.fieldDefs];

    this.fetchProject();
    this.fetchProjectTemplate();
  }

  ngOnInit() {
    this.setupPanel();
    this.checkTable();

    this.offersService.offers$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((offers: Offer[]) => {
      this.offersDataSource = new MatTableDataSource(offers);
      this.applySortToDataSource();
      this.rowData = offers;
      this.setupColumns();
      this.changeDetectorRef.detectChanges();
    });

    this.fetchOffers();

    this.setColDefs();
  }

  ngAfterViewChecked(): void {
    this.applySortToDataSource();
  }

  private fetchOffers(extraParams?: HttpParams): void {
    this.offersService.getOffers(extraParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(noop);
  }

  private setupPanel(): void {
    this.titleFieldDef = {
      name: 'offers.panel_name',
      label: this.panelDef?.title,
      type: 'text',
    };

    this.panelColor = this.panelDef?.color;
  }

  private setupColumns(): void {
    const defaultColumns = ['workflow_state', 'offer_price', 'offer_type', 'sent_at', 'closing_date'];

    const columnsToUse =
      this.panelDef.meta?.fields ??
      (this._projectTemplate.definition?.offers_panel_columns
        ? JSON.parse(this._projectTemplate.definition.offers_panel_columns)
        : defaultColumns);

    this.displayColumns = removeArchivedFieldsFromTableDefinition(columnsToUse, this.fieldDefs);
  }

  private fetchProject(): void {
    this.projectsService.project$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((project) => {
      this.project = project;
      this.offersService.projectId = this.project.id;
    });
  }

  private fetchProjectTemplate(): void {
    this.projectsService.projectTemplate$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((template) => {
      this._projectTemplate = template;
    });
  }

  private closeDisplayFieldsPanel(): void {
    if (this._fieldDefsPanelOverlayRef && this._fieldDefsPanelOverlayRef.hasAttached()) {
      this._fieldDefsPanelOverlayRef.detach();
      this.filteredFieldDefs = this.fieldDefs;
    }

    if (this._fieldsDefsPanelTemplatePortal && this._fieldsDefsPanelTemplatePortal.isAttached) {
      this._fieldsDefsPanelTemplatePortal.detach();
    }
  }

  private removeDisplayColumn(fieldName: string): void {
    this.displayColumns.splice(
      this.displayColumns.findIndex((item) => item === fieldName),
      1,
    );
    this.saveColumns();

    this.cdRef.detectChanges();
  }

  private addDisplayColumn(fieldName: string): void {
    let index = this.displayColumns.findIndex((item) => item === fieldName);
    if (index == -1) {
      this.displayColumns.unshift(fieldName);
      this.saveColumns();
    }
    this.cdRef.detectChanges();
  }

  private saveColumns(): void {
    this.panelDef = {
      ...this.panelDef,
      meta: {
        ...this.panelDef?.meta,
        fields: this.displayColumns,
      },
    };

    // Uncomment below line after offers_panel_columns key/value is removed from all project templates.
    // this.onEditPanelDef.emit(this.panelDef);

    // Remove below code after offers_panel_columns key/value is removed from all project templates.
    delete this._projectTemplate.definition?.offers_panel_columns;

    this.projectsService
      .updateProjectTemplate(this._projectTemplate)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.onEditPanelDef.emit(this.panelDef);
          this.cdRef.detectChanges();
        }),
      )
      .subscribe(noop);
  }

  private setColDefs(): void {
    if (this.newTable) {
      this.columnDefs = this.displayColumns.map((column) => this.lookupField(column));
      this.setColumnsAttributes();
    }
  }

  private setColumnsAttributes(): void {
    this.columnDefs.map((fieldDef) => {
      if (fieldDef) {
        fieldDef.tsTableOptions = {
          ...fieldDef.tsTableOptions,
          sortable: true,
          resizable: true,
          editable: false,
          flex: 1,
        };

        if (fieldDef.name === 'workflow_state') {
          fieldDef.tsTableOptions.cellRenderer = TableStatusRenderer;
          fieldDef.tsTableOptions.cellRendererParams = {
            statusColors: {
              draft: '#6B7280',
              submitted: '#3F83F8',
              rejected: '#F05252',
              accepted: '#0E9F6E',
            },
          };
        }
      }
    });
  }

  private checkTable(): void {
    this.newTable = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_TABLE));
  }

  applySortToDataSource(): void {
    if (this.offersDataSource && this.sort && !this.newTable) {
      this.offersDataSource.sort = this.sort;
    }
  }

  dropColumns(event: CdkDragDrop<any[]>): void {
    const { previousIndex, currentIndex } = event;

    if (currentIndex !== previousIndex) {
      moveItemInArray(this.displayColumns, previousIndex, currentIndex);
      this.saveColumns();
    }
  }

  toggleDisplayColumn(fieldName: string, change: MatCheckboxChange): void {
    if (change.checked) this.addDisplayColumn(fieldName);
    else this.removeDisplayColumn(fieldName);
  }

  lookupField(name: string): FieldDef | undefined {
    return this.fieldDefs.find((fieldDef) => fieldDef.name === name);
  }

  openFieldDefsPanel(): void {
    this._fieldDefsPanelOverlayRef = this.overlay.create({
      backdropClass: '',
      hasBackdrop: true,
      scrollStrategy: this.overlay.scrollStrategies.block(),
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this._fieldDefsPanelOrigin._elementRef.nativeElement)
        .withFlexibleDimensions()
        .withViewportMargin(64)
        .withLockedPosition()
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
        ]),
    });

    this._fieldDefsPanelOverlayRef
      .attachments()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this._fieldDefsPanelOverlayRef.overlayElement.querySelector('input').focus();
      });

    this._fieldsDefsPanelTemplatePortal = new TemplatePortal(this._fieldDefsPanel, this.viewContainerRef);

    this._fieldDefsPanelOverlayRef.attach(this._fieldsDefsPanelTemplatePortal);

    this._fieldDefsPanelOverlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.closeDisplayFieldsPanel();
      });
  }

  filterFields(event: InputEvent): void {
    const searchValue = (event.target as HTMLInputElement).value.toLowerCase().trim();

    if (!searchValue) {
      this.filteredFieldDefs = [...this.fieldDefs];
      return;
    }

    this.filteredFieldDefs = this.fieldDefs.filter((fieldDef) => {
      const fieldLabel = fieldDef.label.toLowerCase();
      // Exact match for complete label
      if (fieldLabel === searchValue) {
        return true;
      }
      // Partial match for incomplete search
      return fieldLabel.includes(searchValue);
    });
  }

  openAddOfferModal(): void {
    this.matDialog
      .open(AddOfferComponent, {
        data: {
          project: this.project,
          projectTemplate: this._projectTemplate,
          panelDef: this.panelDef,
          options: this.data.options,
          account: this.account,
        } as OffersDialogDataModel,
        maxWidth: '50vw',
        width: '100%',
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(noop);
  }

  openEditOfferModel(offer: Offer): void {
    this.matDialog
      .open(AddOfferComponent, {
        data: {
          project: this.project,
          projectTemplate: this._projectTemplate,
          panelDef: this.panelDef,
          options: this.data.options,
          offer: offer,
          account: this.account,
        } as OffersDialogDataModel,
        maxWidth: '50vw',
        width: '100%',
      })
      .afterClosed()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((updatedOffer: Partial<Offer>) => !!updatedOffer),
      )
      .subscribe((updatedOffer: Partial<Offer>) => {
        if (updatedOffer) {
          const index = this.rowData.findIndex((o) => o.id === offer.id);
          if (index !== -1) {
            this.rowData[index] = { ...this.rowData[index], ...updatedOffer };
          }
          if (this.gridApi) {
            this.gridApi.setGridOption('rowData', [...this.rowData]);
          }
        }
      });
  }

  canChangeVisibleColumns(): boolean {
    if (!this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return this.store.selectSnapshot(UserState.hasPermission(Permission.PROJECT_PIPELINE_CHANGE_VISIBLE_COLUMNS));
    }

    return true;
  }

  onColumnMoved(event: any): void {
    TsTableService.moveColumns(event, this.displayColumns, [], () => {
      this.saveColumns();
    });
  }

  onSortChanged(event: SortChangedEvent): void {
    const sortedColumn = event.columns.find((col) => col.getSort());

    const sortSign = sortedColumn?.getSort() === 'asc' ? '' : '-';

    if (sortedColumn?.getColDef()?.field && sortedColumn?.getSort()) {
      this.sortState = `${sortSign}${sortedColumn.getColDef().field}`;
      this.fetchOffers(new HttpParams().set('sort', this.sortState));
    }
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
  }

  trackByFn(index: number, item: Offer): number {
    return item.id || index;
  }
}
